import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeShiftChanges } from '../lib/realtime';

interface StintRow {
  event: 'login' | 'logout';
  occurred_at: string;
}

/**
 * Closed logout→login gaps today (≥1 min) — the driver's logged-out windows
 * during the working day. Same math as the mobile app's utloggning chips, so
 * dashboard and phone report identical numbers.
 */
export function useUtloggadToday(driverId: string | undefined) {
  const [utloggadMin, setUtloggadMin] = useState(0);
  const [utloggadCount, setUtloggadCount] = useState(0);

  const refetch = useCallback(async () => {
    if (!driverId) return;
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const { data } = await supabase
      .from('session_stints')
      .select('event, occurred_at')
      .eq('driver_id', driverId)
      .gte('occurred_at', start.toISOString())
      .order('occurred_at', { ascending: true });
    const stints = (data ?? []) as StintRow[];

    const cap = Date.now();
    let lastLogout: number | null = null;
    let mins = 0;
    let count = 0;
    for (const s of stints) {
      const t = new Date(s.occurred_at).getTime();
      if (s.event === 'logout') {
        lastLogout = t;
      } else {
        if (lastLogout != null) {
          const m = Math.round((Math.min(t, cap) - lastLogout) / 60000);
          if (m >= 1) {
            mins += m;
            count += 1;
          }
        }
        lastLogout = null;
      }
    }
    setUtloggadMin(mins);
    setUtloggadCount(count);
  }, [driverId]);

  useEffect(() => {
    if (!driverId) return;
    refetch();
    const timer = setInterval(refetch, 30000);
    const unsub = subscribeShiftChanges(driverId, refetch);
    return () => {
      clearInterval(timer);
      unsub();
    };
  }, [driverId, refetch]);

  return { utloggadMin, utloggadCount };
}
